import { useState, useMemo } from 'react';
import { useReportData } from '../hooks/useReportData';
import { ReportBarChart } from '../components/report/ReportBarChart';
import { ReportPieChart } from '../components/report/ReportPieChart';
import { ReportDrillDown } from '../components/report/ReportDrillDown';
import { ReportSection } from '../components/report/ReportSection';
import { KpiCard } from '../components/KpiCard';
import { fmtEur, fmtEur2 } from '../components/report/formatters';
import type { ReportParams } from '../types/widget';

interface HistoRow {
    mois: string;
    fournisseur?: string;
    client_code?: string;
    client_label?: string;
    montant_restant_du: number;
}

interface PieRow {
    tranche: string;
    tri: number;
    nb_factures: number;
    montant_restant_du: number;
}

export function SuiviReglements({ filters, variant }: { filters: ReportParams; variant: 'client' | 'prestataire' }) {
    const [drillFilter, setDrillFilter] = useState<{ mois?: string; tranche?: string } | null>(null);

    const histoEndpoint = `/api/emis/reglements/${variant}/histogramme`;
    const pieEndpoint = `/api/emis/reglements/${variant}/pie`;
    const drillEndpoint = `/api/emis/reglements/${variant}/drill-down`;

    const histo = useReportData<HistoRow>(histoEndpoint, filters);
    const pie = useReportData<PieRow>(pieEndpoint, filters);

    // KPIs
    const kpis = useMemo(() => {
        const pieRows = pie.data;
        const histoRows = histo.data;
        if (!pieRows.length && !histoRows.length) return null;
        const totalFactures = pieRows.reduce((s, r) => s + Number(r.nb_factures), 0);
        const totalRestantDu = pieRows.reduce((s, r) => s + Number(r.montant_restant_du), 0);
        const entityKey: 'fournisseur' | 'client_code' = variant === 'prestataire' ? 'fournisseur' : 'client_code';
        const nbEntities = new Set(histoRows.map((r) => r[entityKey])).size;
        return { totalFactures, totalRestantDu, nbEntities };
    }, [pie.data, histo.data, variant]);

    const histoData = useMemo(() => {
        const months = [...new Set(histo.data.map((r) => r.mois))].sort();
        const entityKey: 'fournisseur' | 'client_code' = variant === 'prestataire' ? 'fournisseur' : 'client_code';
        const entities = [...new Set(histo.data.map((r) => r[entityKey] as string))];
        const byKey = new Map(
            histo.data.map((r) => [`${r[entityKey]}-${r.mois}`, r]),
        );

        const series = entities.map((entity) => ({
            name: variant === 'client'
                ? histo.data.find((r) => r.client_code === entity)?.client_label ?? entity
                : entity,
            data: months.map((m) => Number(byKey.get(`${entity}-${m}`)?.montant_restant_du ?? 0)),
            stack: 'total',
        }));
        return { months, series };
    }, [histo.data, variant]);

    // Deux jeux de données : un pour le camembert "nombre" (avec suffixe lisible), un pour le camembert "montant" (tooltip formaté en €).
    const pieDataNombre = useMemo(
        () => pie.data.map((r) => ({
            name: `${r.tranche} (${r.nb_factures})`,
            value: Number(r.nb_factures),
            sort_order: r.tri,
            tranche: r.tranche,
        })),
        [pie.data],
    );

    const pieDataMontant = useMemo(
        () => pie.data.map((r) => ({
            name: r.tranche,
            value: Number(r.montant_restant_du),
            sort_order: r.tri,
            tranche: r.tranche,
        })),
        [pie.data],
    );

    // Drill-down indépendant du format d'affichage : lookup sur la tranche brute.
    const handleSliceClick = (name: string) => {
        const row = pie.data.find((r) => name === r.tranche || name.startsWith(`${r.tranche} (`));
        if (row) setDrillFilter({ tranche: row.tranche });
    };

    const drillParams = useMemo(() => ({ ...filters, ...drillFilter }), [filters, drillFilter]);

    const drillColumns = variant === 'prestataire'
        ? [
              { key: 'fournisseur', label: 'Fournisseur', filterable: true },
              { key: 'numero_facture', label: 'N° facture', filterable: true },
              { key: 'libelle', label: 'Libellé', filterable: true },
              { key: 'date_facture', label: 'Date facture' },
              { key: 'date_echeance', label: 'Échéance' },
              { key: 'montant_ht', label: 'Montant HT', align: 'right' as const, format: (v: unknown) => fmtEur2(Number(v)), totalKey: 'montant_ht', formatTotal: (v: number) => fmtEur2(v) },
              { key: 'restant_du_ht', label: 'Restant dû HT', align: 'right' as const, format: (v: unknown) => fmtEur2(Number(v)), totalKey: 'restant_du_ht', formatTotal: (v: number) => fmtEur2(v) },
              { key: 'anciennete_jours', label: 'Ancienneté (j)', align: 'right' as const },
              { key: 'tranche', label: 'Tranche', filterable: true },
          ]
        : [
              { key: 'pennylane_customer_name', label: 'Client (tiers)', filterable: true, format: (v: unknown) => v ? String(v) : '—' },
              { key: 'client_code', label: 'Client (catégorie)', filterable: true },
              { key: 'client_label', label: 'Nom client', filterable: true },
              { key: 'numero_facture', label: 'N° facture', filterable: true },
              { key: 'libelle', label: 'Libellé', filterable: true },
              { key: 'date_facture', label: 'Date facture' },
              { key: 'date_echeance', label: 'Échéance' },
              { key: 'montant_ht', label: 'Montant HT', align: 'right' as const, format: (v: unknown) => fmtEur2(Number(v)), totalKey: 'montant_ht', formatTotal: (v: number) => fmtEur2(v) },
              { key: 'restant_du_ht', label: 'Restant dû HT', align: 'right' as const, format: (v: unknown) => fmtEur2(Number(v)), totalKey: 'restant_du_ht', formatTotal: (v: number) => fmtEur2(v) },
              { key: 'anciennete_jours', label: 'Ancienneté (j)', align: 'right' as const },
              { key: 'tranche', label: 'Tranche', filterable: true },
          ];

    const drillTitle = `Factures ${variant === 'prestataire' ? 'fournisseurs' : 'clients'}${drillFilter?.mois ? ` — ${drillFilter.mois}` : ''}${drillFilter?.tranche ? ` — ${drillFilter.tranche}` : ''}`;

    // Tranches ordonnées pour navigation
    const orderedTranches = useMemo(
        () => [...pie.data].sort((a, b) => a.tri - b.tri).map((r) => r.tranche),
        [pie.data],
    );

    const handleTrancheChange = (tranche: string) => {
        setDrillFilter({ tranche });
    };

    return (
        <div className="space-y-6">
            {/* KPI Strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KpiCard label="Factures en cours" value={kpis?.totalFactures ?? '—'} loading={pie.loading} />
                <KpiCard label="Restant dû total" value={kpis ? fmtEur(kpis.totalRestantDu) : '—'} loading={pie.loading} />
                <KpiCard label={variant === 'prestataire' ? 'Fournisseurs' : 'Clients'} value={kpis?.nbEntities ?? '—'} loading={histo.loading} />
            </div>

            {/* Layout 2 colonnes : histo + pie */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ReportSection
                    title={`Encours ${variant === 'prestataire' ? 'prestataires' : 'clients'} par mois`}
                    loading={histo.loading}
                    error={histo.error}
                >
                    <ReportBarChart
                        title={`Restant dû par mois`}
                        categories={histoData.months}
                        series={histoData.series}
                        valueFormatter={fmtEur}
                        tooltipShowStackTotal
                        onBarClick={(_, mois) => setDrillFilter({ mois })}
                    />
                </ReportSection>

                <ReportSection
                    title="Répartition par ancienneté"
                    loading={pie.loading}
                    error={pie.error}
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <ReportPieChart
                            title="Nombre de factures par tranche"
                            data={pieDataNombre}
                            height={300}
                            onSliceClick={handleSliceClick}
                        />
                        <ReportPieChart
                            title="Montant des factures par tranche"
                            data={pieDataMontant}
                            height={300}
                            valueFormatter={fmtEur}
                            onSliceClick={handleSliceClick}
                        />
                    </div>
                </ReportSection>
            </div>

            {/* Drill-down sous les graphiques */}
            {(drillFilter?.mois || drillFilter?.tranche) && (
                <ReportDrillDown
                    title={drillTitle}
                    endpoint={drillEndpoint}
                    params={drillParams}
                    enabled={true}
                    onClose={() => setDrillFilter(null)}
                    columns={drillColumns}
                    {...(drillFilter?.tranche ? {
                        tranches: orderedTranches,
                        currentTranche: drillFilter.tranche,
                        onTrancheChange: handleTrancheChange,
                    } : {})}
                />
            )}
        </div>
    );
}
